/**
 * GENERATED by bin/generate.ts — do not edit. Regenerate with `bun run generate`.
 *
 * Every entry is a named formula (spec §19.4c): a pure $expression body over $args/<name>
 * parameters, verified against the blessed operator and global sets at generation time.
 */

import type { CemParameter, JxExpressionNode } from "@jxsuite/schema/types";

export interface FormulaCatalogEntry {
  name: string;
  description: string;
  parameters: (string | CemParameter)[];
  expression: JxExpressionNode;
}

export const catalog: FormulaCatalogEntry[] = [
  {
    description: "Arithmetic mean of an array of numbers; 0 for an empty array.",
    expression: {
      operator: "?:",
      target: {
        $ref: "$args/values/length",
      },
      value: {
        operator: "/",
        target: {
          operator: "reduce",
          target: {
            $ref: "$args/values",
          },
          initial: 0,
          value: {
            operator: "+",
            target: {
              $ref: "$reduce/acc",
            },
            value: {
              $ref: "$map/item",
            },
          },
        },
        value: {
          $ref: "$args/values/length",
        },
      },
      initial: 0,
    },
    name: "average",
    parameters: [
      {
        name: "values",
        type: {
          text: "number[]",
        },
      },
    ],
  },
  {
    description:
      "Uppercase the first character of a string, leaving the rest unchanged (the CSS text-transform: capitalize shape for a single word).",
    expression: {
      operator: "+",
      target: {
        operator: "toUpperCase",
        target: {
          operator: "charAt",
          target: {
            $ref: "$args/text",
          },
          value: 0,
        },
      },
      value: {
        operator: "slice",
        target: {
          $ref: "$args/text",
        },
        value: 1,
      },
    },
    name: "capitalize",
    parameters: [
      {
        name: "text",
        type: {
          text: "string",
        },
      },
    ],
  },
  {
    description:
      "Bound a number to the [min, max] range (the TC39 Math.clamp shape): returns min when below, max when above, the value itself otherwise.",
    expression: {
      operator: "call",
      target: {
        $ref: "window#/Math/min",
      },
      value: [
        {
          operator: "call",
          target: {
            $ref: "window#/Math/max",
          },
          value: [
            {
              $ref: "$args/value",
            },
            {
              $ref: "$args/min",
            },
          ],
        },
        {
          $ref: "$args/max",
        },
      ],
    },
    name: "clamp",
    parameters: [
      {
        name: "value",
        type: {
          text: "number",
        },
      },
      {
        name: "min",
        type: {
          text: "number",
        },
      },
      {
        name: "max",
        type: {
          text: "number",
        },
      },
    ],
  },
  {
    description:
      "A copy of an array with the falsy elements removed (Array.prototype.filter on truthiness).",
    expression: {
      operator: "filter",
      target: {
        $ref: "$args/values",
      },
      value: {
        operator: "!",
        target: {
          operator: "!",
          target: {
            $ref: "$map/item",
          },
        },
      },
    },
    name: "compact",
    parameters: [
      {
        name: "values",
        type: {
          text: "unknown[]",
        },
      },
    ],
  },
  {
    description: "The length of an array or string, or 0 when the value is missing.",
    expression: {
      operator: "??",
      target: {
        $ref: "$args/values/length",
      },
      value: 0,
    },
    name: "count",
    parameters: [
      {
        name: "values",
        type: {
          text: "unknown[] | string",
        },
      },
    ],
  },
  {
    description: "The first element of an array (index 0), or null when empty.",
    expression: {
      operator: "??",
      target: {
        $ref: "$args/values/0",
      },
      value: null,
    },
    name: "first",
    parameters: [
      {
        name: "values",
        type: {
          text: "unknown[]",
        },
      },
    ],
  },
  {
    description:
      'The uppercase first letters of each whitespace-separated word, joined (e.g. "ada lovelace" → "AL").',
    expression: {
      operator: "join",
      target: {
        operator: "map",
        target: {
          operator: "split",
          target: {
            $ref: "$args/name",
          },
          value: " ",
        },
        value: {
          operator: "toUpperCase",
          target: {
            operator: "charAt",
            target: {
              $ref: "$map/item",
            },
            value: 0,
          },
        },
      },
      value: "",
    },
    name: "initials",
    parameters: [
      {
        name: "name",
        type: {
          text: "string",
        },
      },
    ],
  },
  {
    description:
      "True when an array or string has no elements (length 0 — a missing value counts as empty).",
    expression: {
      operator: "===",
      target: {
        operator: "??",
        target: {
          $ref: "$args/value/length",
        },
        value: 0,
      },
      value: 0,
    },
    name: "isEmpty",
    parameters: [
      {
        name: "value",
        type: {
          text: "unknown[] | string",
        },
      },
    ],
  },
  {
    description: "The last element of an array (Array.prototype.at(-1)), or undefined when empty.",
    expression: {
      operator: "at",
      target: {
        $ref: "$args/values",
      },
      value: -1,
    },
    name: "last",
    parameters: [
      {
        name: "values",
        type: {
          text: "unknown[]",
        },
      },
    ],
  },
  {
    description:
      "The largest number in an array (Math.max folded over the values); undefined for an empty array.",
    expression: {
      operator: "reduce",
      target: {
        $ref: "$args/values",
      },
      initial: {
        $ref: "$args/values/0",
      },
      value: {
        operator: "call",
        target: {
          $ref: "window#/Math/max",
        },
        value: [
          {
            $ref: "$reduce/acc",
          },
          {
            $ref: "$map/item",
          },
        ],
      },
    },
    name: "max",
    parameters: [
      {
        name: "values",
        type: {
          text: "number[]",
        },
      },
    ],
  },
  {
    description:
      "The smallest number in an array (Math.min folded over the values); undefined for an empty array.",
    expression: {
      operator: "reduce",
      target: {
        $ref: "$args/values",
      },
      initial: {
        $ref: "$args/values/0",
      },
      value: {
        operator: "call",
        target: {
          $ref: "window#/Math/min",
        },
        value: [
          {
            $ref: "$reduce/acc",
          },
          {
            $ref: "$map/item",
          },
        ],
      },
    },
    name: "min",
    parameters: [
      {
        name: "values",
        type: {
          text: "number[]",
        },
      },
    ],
  },
  {
    description: "part / whole as a percentage (0 when the whole is 0 or missing).",
    expression: {
      operator: "?:",
      target: {
        $ref: "$args/whole",
      },
      value: {
        operator: "*",
        target: {
          operator: "/",
          target: {
            $ref: "$args/part",
          },
          value: {
            $ref: "$args/whole",
          },
        },
        value: 100,
      },
      initial: 0,
    },
    name: "percent",
    parameters: [
      {
        name: "part",
        type: {
          text: "number",
        },
      },
      {
        name: "whole",
        type: {
          text: "number",
        },
      },
    ],
  },
  {
    description:
      "Round a number to the given decimal places (Math.round against a Math.pow(10, digits) scale).",
    expression: {
      operator: "/",
      target: {
        operator: "call",
        target: {
          $ref: "window#/Math/round",
        },
        value: [
          {
            operator: "*",
            target: {
              $ref: "$args/value",
            },
            value: {
              operator: "call",
              target: {
                $ref: "window#/Math/pow",
              },
              value: [
                10,
                {
                  $ref: "$args/digits",
                },
              ],
            },
          },
        ],
      },
      value: {
        operator: "call",
        target: {
          $ref: "window#/Math/pow",
        },
        value: [
          10,
          {
            $ref: "$args/digits",
          },
        ],
      },
    },
    name: "roundTo",
    parameters: [
      {
        name: "value",
        type: {
          text: "number",
        },
      },
      {
        name: "digits",
        type: {
          text: "number",
        },
        default: 0,
      },
    ],
  },
  {
    description: "Add every number in an array (Array.prototype.reduce with +, seeded at 0).",
    expression: {
      operator: "reduce",
      target: {
        $ref: "$args/values",
      },
      initial: 0,
      value: {
        operator: "+",
        target: {
          $ref: "$reduce/acc",
        },
        value: {
          $ref: "$map/item",
        },
      },
    },
    name: "sum",
    parameters: [
      {
        name: "values",
        type: {
          text: "number[]",
        },
      },
    ],
  },
  {
    description:
      "Shorten a string to at most the given length, appending a horizontal ellipsis when cut.",
    expression: {
      operator: "?:",
      target: {
        operator: ">",
        target: {
          $ref: "$args/text/length",
        },
        value: {
          $ref: "$args/length",
        },
      },
      value: {
        operator: "+",
        target: {
          operator: "slice",
          target: {
            $ref: "$args/text",
          },
          value: [
            0,
            {
              $ref: "$args/length",
            },
          ],
        },
        value: "…",
      },
      initial: {
        $ref: "$args/text",
      },
    },
    name: "truncate",
    parameters: [
      {
        name: "text",
        type: {
          text: "string",
        },
      },
      {
        name: "length",
        type: {
          text: "number",
        },
      },
    ],
  },
] as unknown as FormulaCatalogEntry[];
